import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  useTheme,
  useMediaQuery,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  TextField
} from '@mui/material';
import {
  Menu as MenuIcon,
  Handshake,
  Store,
  People,
  Chat,
  LocationOn,
  Person,
  Settings,
  Logout,
  Notifications,
  Security,
  Feedback,
  Forum,
  AutoStories,
  Search as SearchIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../hooks/useSocket';
import FeedbackDialog from '../UI/FeedbackDialog';
import { useTranslation } from '../../context/LanguageContext';
import NebenanHilfeLogo from '../UI/NebenanHilfeLogo';

const drawerWidth = 240;

const DashboardLayout = ({ children, currentSection, onSectionChange }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuth();
  const { unreadCount } = useSocket();
  const { t } = useTranslation();
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/dashboard/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const menuItems = [
    { id: 'miteinander', label: 'Miteinander', icon: <Handshake /> },
    { id: 'marktplatz', label: 'Marktplatz', icon: <Store /> },
    { id: 'forum', label: 'Forum', icon: <Forum /> },
    { id: 'wissen', label: 'Wissen & Blog', icon: <AutoStories /> },
    { id: 'freunde', label: 'Freunde', icon: <People /> },
    { id: 'chat', label: 'Chat', icon: <Chat /> },
    { id: 'location', label: 'Standort', icon: <LocationOn /> },
    { id: 'profile', label: 'Profil', icon: <Person /> },
    { id: 'settings', label: 'Einstellungen', icon: <Settings /> },
    { id: 'security', label: 'Sicherheit', icon: <Security /> }
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const drawer = (
    <div>
      <Toolbar sx={{ px: 2, minHeight: 70 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%' }}>
          <NebenanHilfeLogo size="medium" lightText={theme.palette.mode === 'dark'} />
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              selected={currentSection === item.id}
              onClick={() => {
                onSectionChange(item.id);
                if (isMobile) setMobileOpen(false);
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: currentSection === item.id ? 'primary.main' : 'inherit' }}>
                {item.id === 'chat' ? (
                  <Badge badgeContent={unreadCount} color="error">
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText 
                primary={t(item.id)} 
                primaryTypographyProps={{ sx: { textTransform: 'capitalize', fontWeight: currentSection === item.id ? 600 : 500 } }} 
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => setFeedbackOpen(true)}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Feedback />
            </ListItemIcon>
            <ListItemText primary="Feedback" />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Current page title */}
          <Typography 
            variant="subtitle1" 
            noWrap 
            component="div" 
            sx={{ 
              display: { xs: 'none', sm: 'block' }, 
              mr: 2, 
              fontWeight: 600,
              color: 'text.primary',
              letterSpacing: '-0.2px'
            }}
          >
            {t(currentSection) || 'Dashboard'}
          </Typography>

          {/* Search Bar */}
          <Box 
            component="form" 
            onSubmit={handleSearchSubmit}
            sx={{ 
              position: 'relative', 
              borderRadius: '6px', 
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              '&:hover': { borderColor: 'text.disabled' },
              mr: 2,
              ml: 0,
              width: '100%',
              maxWidth: { xs: '100%', sm: '300px' },
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.15s'
            }}
          >
            <Box sx={{ p: '0 12px', height: '100%', position: 'absolute', pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SearchIcon color="action" fontSize="small" />
            </Box>
            <TextField
              size="small"
              placeholder="Suche..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: { 
                  pl: '40px', 
                  pr: '10px', 
                  py: '6px',
                  width: '100%',
                  fontSize: '0.9rem'
                }
              }}
              sx={{ width: '100%' }}
            />
          </Box>
          
          <Box sx={{ flexGrow: 1 }} />

          <IconButton color="inherit" sx={{ mr: 1 }}>
            <Badge badgeContent={3} color="error">
              <Notifications />
            </Badge>
          </IconButton>

          <IconButton
            onClick={handleMenuClick}
            sx={{ p: 0 }}
          >
            <Avatar sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText', fontSize: '0.875rem', fontWeight: 600 }}>
              {user?.firstName && user?.lastName ? (
                `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
              ) : (
                <Person fontSize="small" />
              )}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
          >
            <MenuItem disabled>
              <Typography variant="subtitle2">
                {user?.firstName} {user?.lastName}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => onSectionChange('profile')}>
              <Person sx={{ mr: 1 }} />
              Profil
            </MenuItem>
            <MenuItem onClick={() => onSectionChange('settings')}>
              <Settings sx={{ mr: 1 }} />
              Einstellungen
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 1 }} />
              Abmelden
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          mb: isMobile ? 7 : 0 // Padding for bottom nav
        }}
      >
        {children}
      </Box>


      {isMobile && (
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100 }} elevation={3}>
          <BottomNavigation
            showLabels
            value={currentSection}
            onChange={(event, newValue) => {
              onSectionChange(newValue);
            }}
          >
            <BottomNavigationAction label={t('feed')} value="miteinander" icon={<Handshake />} />
            <BottomNavigationAction label={t('marketplace')} value="marktplatz" icon={<Store />} />
            <BottomNavigationAction 
              label={t('chat')} 
              value="chat" 
              icon={
                <Badge badgeContent={unreadCount} color="error">
                  <Chat />
                </Badge>
              } 
            />
            <BottomNavigationAction label={t('profile')} value="profile" icon={<Person />} />
          </BottomNavigation>
        </Paper>
      )}

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </Box>
  );
};

export default DashboardLayout;
